import {
  FlaskConical, BookOpen, ShieldCheck, Leaf, Microscope,
  FileText, Activity, AlertTriangle,
} from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  TrustPage, TrustHero, TrustSection, IconCardGrid,
  ClosingCta,
} from "../components/TrustLayout";

/**
 * /science — ingredient transparency + wellness science page.
 * EEAT-friendly tone, NO medical claims.
 */
export default function SciencePage() {
  useSeo({
    title: "Science & ingredients — Feel Greats wellness library",
    description:
      "Transparent ingredient education, evidence-based wellness science, and the principles behind our personalised recommendations. Wellness, never medical claims.",
    canonical: `${PROD_DOMAIN}/science`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Feel Greats Science & Ingredients",
        "url": `${PROD_DOMAIN}/science`,
        "about": {
          "@type": "Thing",
          "name": "Evidence-based wellness ingredient education",
        },
      },
    ],
  });

  return (
    <TrustPage testid="science-page">
      <TrustHero
        eyebrow="Science & ingredients"
        headline="Transparent ingredients. Honest evidence."
        highlightWord="Honest"
        body="Every product we recommend comes with a real ingredient PDF — not marketing speak. Every claim is wellness-level, never medical. And every recommendation is supported by the same evidence we'd want if our own family asked."
        primaryCta={{ to: "/shop", label: "Browse the wellness range", testid: "science-hero-shop" }}
        secondaryCta={{ to: "/learn", label: "Explore the library", testid: "science-hero-learn" }}
        testid="science-hero"
      />

      {/* The principles */}
      <TrustSection
        eyebrow="Our science principles"
        title="Five rules we don't break."
        body="The wellness industry has a credibility problem. We try to do the opposite of what gives it a bad name."
        tone="default"
        testid="science-principles"
      >
        <IconCardGrid
          cols={3}
          cards={[
            {
              icon: ShieldCheck,
              eyebrow: "Rule 1",
              title: "No disease claims.",
              body: "We never promise to 'cure', 'treat', or 'prevent' anything. Even if an ingredient has clinical evidence in a specific area, we describe it in supportive, wellness-level terms.",
            },
            {
              icon: FileText,
              eyebrow: "Rule 2",
              title: "Real PDFs, every product.",
              body: "Every product page links to the actual ingredient sheet and supplement-facts PDF. Read them. Ask questions. Bring them to your GP.",
            },
            {
              icon: Microscope,
              eyebrow: "Rule 3",
              title: "Evidence > stories.",
              body: "Anecdote is interesting; evidence is what we lean on. Where evidence is thin, we say so — and recommend habits first, supplements second.",
            },
            {
              icon: Leaf,
              eyebrow: "Rule 4",
              title: "Clean labels matter.",
              body: "We only recommend products with transparent ingredient lists, identifiable sources, and no fillers we can't pronounce.",
            },
            {
              icon: AlertTriangle,
              eyebrow: "Rule 5",
              title: "Safety always wins.",
              body: "If you're pregnant, nursing, on medication, or managing a chronic condition — pause and check with your clinician. We'll wait. Wellness has time.",
            },
            {
              icon: BookOpen,
              eyebrow: "Bonus",
              title: "We update our library.",
              body: "Science moves. When new evidence shifts a recommendation, we update the article — and email members who started with it.",
            },
          ]}
        />
      </TrustSection>

      {/* Ingredient transparency */}
      <TrustSection
        eyebrow="Ingredient transparency"
        title="Five questions we want you to ask."
        body="Before any product enters our recommendation list, our mentor team puts it through these five filters. We answer them publicly on every product page."
        tone="amber"
        testid="science-questions"
      >
        <IconCardGrid
          cols={2}
          cards={[
            { icon: FlaskConical, title: "1. What's actually in it?", body: "Full ingredient list with sources. No 'proprietary blends' that hide the dose. We want to know the milligrams." },
            { icon: Activity, title: "2. What does the evidence say?", body: "We summarise the best human studies — and the limitations. If a benefit comes from one small study, we say so." },
            { icon: ShieldCheck, title: "3. Who shouldn't take it?", body: "Pregnant, nursing, immunosuppressed, on blood thinners, kidney/liver concerns — every product page flags the relevant caveats." },
            { icon: Microscope, title: "4. How is it manufactured?", body: "We favour manufacturers with third-party testing, GMP-certified facilities, and ingredient traceability." },
            { icon: Leaf, title: "5. Is there a less-is-more option?", body: "Many ingredients work below the highest-tolerated dose. We prefer products that respect that — not 'mega' formulas." },
            { icon: BookOpen, title: "Bonus — could food do this first?", body: "Often, yes. We say so. A handful of brazil nuts beats a selenium pill for most people. Supplements fill gaps; food does the rest." },
          ]}
        />
      </TrustSection>

      {/* The 'wellness, never medical' disclaimer */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="science-disclaimer">
        <div className="rounded-3xl border border-amber-200/60 bg-amber-50/40 p-6 sm:p-9">
          <p className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-semibold">A clear note</p>
          <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-2">
            Feel Greats is wellness — never a medical service.
          </h2>
          <p className="mt-4 text-base text-emerald-900/85 leading-relaxed">
            Our content, assessments, mentors and product suggestions are designed for general wellness support. They are not a substitute for diagnosis or treatment by a licensed clinician. If you are managing a medical condition, taking prescription medication, pregnant, nursing, or under 18, please speak with your GP before adding any new supplement.
          </p>
          <p className="mt-3 text-sm text-emerald-900/75 leading-relaxed">
            Our mentors are trained to recognise when a question is outside our scope and to route you to clinical care when appropriate. We'd rather slow down than overstep.
          </p>
        </div>
      </section>

      <TrustSection
        eyebrow="Ingredient deep-dives"
        title="The wellness library is your science library."
        body="Browse evidence-based deep-dives on the ingredients in our range — and several we deliberately don't carry."
        tone="white"
        testid="science-library"
      >
        <IconCardGrid
          cols={3}
          cards={[
            { icon: BookOpen, title: "Yerba mate — calm energy with focus.", body: "What the studies say, and how it differs from coffee.", cta: { label: "Read the deep-dive", to: "/learn", testid: "science-yerba-link" } },
            { icon: BookOpen, title: "Fibre matrix — beyond regularity.", body: "Bio-available fibre, blood sugar curves, and the bowel honestly.", cta: { label: "Read the deep-dive", to: "/learn", testid: "science-fibre-link" } },
            { icon: BookOpen, title: "Whey protein isolate — clean by design.", body: "How isolate differs from concentrate, and when it actually matters.", cta: { label: "Read the deep-dive", to: "/learn", testid: "science-whey-link" } },
          ]}
        />
      </TrustSection>

      <ClosingCta
        eyebrow="Read more, decide later"
        title="Curious about a specific ingredient?"
        body="Ask our mentor on WhatsApp — we'll send the relevant article or PDF, no signup needed."
      />
    </TrustPage>
  );
}
